import { datadogRum } from '@datadog/browser-rum';
import { getUserState } from '@carvana/core-auth';
import Cookie from '@carvana/stc-cookie';

import { recordGenericEvent, EventActions } from '../../analytics/uiMetrics';

import { Severity } from './datadog.types';
import type { DatadogSpan, LogMessage } from './datadog.types';

type ErrorAnalyticsContext = {
  pageName: string;
  component: string;
  eventData?: Record<string, unknown>;
};

const MODULE_NAME = 'stc-home-ui';
const MODULE_VERSION = `${process.env.APP_VERSION}`;
const TEST_ALLOWED_TRACING_URLS = ['https://apps.carvanatech.com', 'https://apik.carvanatech.com'];

const getAllowedTracingUrls = () =>
  process.env.CARVANA_APP_ENVIRONMENT === 'test' ? TEST_ALLOWED_TRACING_URLS : undefined;

const getTracingConfiguration = () => {
  const allowedTracingUrls = getAllowedTracingUrls();

  if (!allowedTracingUrls) {
    return {};
  }

  return {
    allowedTracingUrls,
    propagateTraceBaggage: true,
    traceContextInjection: 'all' as const,
    traceSampleRate: 100
  };
};

const syncDatadogUser = (userId?: string) => {
  const bcid = Cookie.getBcId();

  if (userId || bcid) {
    datadogRum.setUser({
      ...(userId ? { id: userId } : {}),
      ...(bcid ? { bcid } : {})
    });
    return;
  }

  datadogRum.clearUser();
};

const startPageView = (name: string) => {
  datadogRum.startView({
    name,
    service: MODULE_NAME,
    version: MODULE_VERSION
  });
};

const initializeDatadog = () => {
  const { user } = getUserState();

  // Initialize DataDog RUM for home-ui
  datadogRum.init({
    ...getTracingConfiguration(),
    applicationId: '21e1a5db-649f-479c-b3d6-c51622dca229',
    clientToken: 'pub1d8c8908f469710592832a49e98cf269',
    defaultPrivacyLevel: 'mask-user-input',
    env: process.env.CARVANA_APP_ENVIRONMENT,
    service: MODULE_NAME,
    sessionReplaySampleRate: 100,
    sessionSampleRate: 100,
    site: 'datadoghq.com',
    trackLongTasks: true,
    trackResources: true,
    trackUserInteractions: true,
    trackViewsManually: true,
    version: MODULE_VERSION
  });

  syncDatadogUser(user.user_id);

  datadogRum.addAction('STC Home UI Loaded', {
    mfeData: { name: MODULE_NAME, version: process.env.APP_VERSION }
  });
};

const log = (message: LogMessage) => {
  if (message.severity >= Severity.Error) {
    datadogRum.addError(message.message, message);
  }
  datadogRum.addAction('LogMessage', message);
};

const logError = (err: unknown, message?: string, analyticsContext?: ErrorAnalyticsContext) => {
  datadogRum.addError(err, { message });

  if (analyticsContext) {
    const { pageName, component, eventData = {} } = analyticsContext;
    recordGenericEvent(pageName, component, EventActions.Errored, {
      ...eventData,
      error:
        err instanceof Error
          ? { name: err.name, message: err.message, stack: err.stack }
          : String(err),
      message
    });
  }
};

export class DataDogSpanImpl implements DatadogSpan {
  private readonly measureStart: DOMHighResTimeStamp;

  private readonly start: Date;

  private readonly spanName: string;

  private readonly component: string;

  private measureEnd?: DOMHighResTimeStamp;

  private ended?: Date;

  constructor(component: string, name: string) {
    this.spanName = name;
    this.component = component;
    this.measureStart = performance.now();
    this.start = new Date();
  }

  private defaultParameters() {
    return {
      component: this.component,
      spanName: this.spanName,
      start: this.start
    };
  }

  addEvent(name: string, attributes?: unknown): this {
    const elapsedMs = performance.now() - this.measureStart;
    datadogRum.addAction(`${this.component}:${this.spanName}:${name}`, {
      ...this.defaultParameters(),
      name,
      elapsedMs,
      attributes: attributes ?? {}
    });
    return this;
  }

  addErrorEvent(name: string, attributes?: unknown, severity?: Severity): this {
    const elapsed = performance.now() - this.measureStart;
    datadogRum.addError(`${this.component}:${this.spanName}:${name}`, {
      severity: severity ?? Severity.Error,
      elapsed,
      attributes: attributes ?? {}
    });

    return this;
  }

  end(attributes?: object): void {
    if (this.measureEnd) {
      return;
    }
    this.measureEnd = performance.now();
    this.ended = new Date();
    const elapsedMs = performance.now() - this.measureStart;

    datadogRum.addAction(`${this.component}:${this.spanName}`, {
      ...this.defaultParameters(),
      ended: this.ended,
      elapsedMs,
      ...(attributes || {})
    });
  }
}

const startSpan = (component: string, name: string): DatadogSpan =>
  new DataDogSpanImpl(component, name);

export {
  getAllowedTracingUrls,
  getTracingConfiguration,
  initializeDatadog,
  log,
  logError,
  startPageView,
  startSpan,
  syncDatadogUser
};
